import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlanCopy, getPlanFeatures } from '../../utils/pricingTranslations';
import { getPlanBilling, getPlanCredits, getPricingPlans, PRICING_PRODUCTS, PRICING_SALES_URL } from '../../utils/pricingCatalog';
import './PricingCatalog.css';

export default function PricingCatalog({ defaultProduct = 'ats', products = PRICING_PRODUCTS,
  onSelectPlan, pendingPlan = null, error, currentPlanId, requiredFeature, featureDisplayName, footer }) {
  const { t, i18n } = useTranslation('pricing');
  const id = useId();
  const [selectedProduct, setSelectedProduct] = useState(defaultProduct);
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const available = products.filter(product => getPricingPlans(product, requiredFeature).length);
  const product = available.includes(selectedProduct) ? selectedProduct : available[0] || products[0];
  const plans = getPricingPlans(product, requiredFeature);
  const isRTL = i18n.dir() === 'rtl';
  // Currency stays in one LTR unit in both languages, as on the public pricing pages.
  const usd = amount => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(amount);
  const number = value => new Intl.NumberFormat(isRTL ? 'ar' : 'en-US').format(value);
  const changeTabWithKey = event => {
    let index = available.indexOf(product);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = available.length - 1;
    else if (event.key === 'ArrowRight') index += isRTL ? -1 : 1;
    else if (event.key === 'ArrowLeft') index += isRTL ? 1 : -1;
    else return;
    event.preventDefault();
    const next = available[(index + available.length) % available.length];
    setSelectedProduct(next);
    document.getElementById(`${id}-${next}-tab`)?.focus();
  };

  return <section className="jsq-pricing" dir={isRTL ? 'rtl' : 'ltr'} aria-label={t('titles.eyebrow')}>
    {featureDisplayName && <p className="jsq-pricing-feature"><i className="fa-solid fa-lock" aria-hidden="true" />{t('labels.unlockFeature', { feature: featureDisplayName })}</p>}
    {available.length > 1 && <div className="jsq-pricing-tabs" role="tablist" aria-label={t('labels.products')}>
      {available.map(type => <button type="button" key={type} id={`${id}-${type}-tab`} role="tab"
        aria-selected={product === type} aria-controls={`${id}-panel`} tabIndex={product === type ? 0 : -1}
        onKeyDown={changeTabWithKey} onClick={() => setSelectedProduct(type)} disabled={Boolean(pendingPlan)}>
        {t(`tabs.${type}`)}
      </button>)}
    </div>}
    <header className="jsq-pricing-heading">
      <div><h1>{t(`products.${product}.title`)}</h1><p>{t(`products.${product}.description`)}</p></div>
      <div className="jsq-pricing-billing-wrap">
        <div className="jsq-pricing-billing" role="group" aria-label={t('billing.label')}>
          {['monthly', 'yearly'].map(period => <button key={period} type="button" aria-pressed={billingPeriod === period}
            disabled={Boolean(pendingPlan)} onClick={() => setBillingPeriod(period)}>
            {t(period === 'yearly' ? 'billing.annual' : 'billing.monthly')}
          </button>)}
        </div>
        <span className="jsq-pricing-saving">{t('billing.saveChip')}</span>
      </div>
    </header>
    {error && <p className="jsq-pricing-error" role="alert"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" />{error}</p>}
    <div id={`${id}-panel`} role={available.length > 1 ? 'tabpanel' : undefined}
      aria-labelledby={available.length > 1 ? `${id}-${product}-tab` : undefined}>
      <div key={product} className={`jsq-pricing-grid jsq-pricing-grid--${plans.length}`}>
        {plans.map(plan => {
          const copy = getPlanCopy(t, plan.id, { credits: getPlanCredits(plan) });
          const features = getPlanFeatures(t, plan.id, product, { credits: getPlanCredits(plan), fallbackFeatures: plan.displayFeatures });
          const price = getPlanBilling(plan, billingPeriod);
          const current = currentPlanId === plan.id;
          return <article key={plan.id} className={`jsq-pricing-card${current ? ' jsq-pricing-card--current' : ''}`} aria-labelledby={`${id}-${plan.id}`}>
            <div className="jsq-pricing-intro"><div className="jsq-pricing-plan-name"><h2 id={`${id}-${plan.id}`}>{copy.title}</h2>{current && <span>{t('cta.currentPlan')}</span>}</div><p>{copy.description}</p></div>
            <div className="jsq-pricing-cost">
              <div className="jsq-pricing-amount"><bdi dir="ltr">{usd(price.monthlyEquivalent)}</bdi><span>{t('labels.perMonth')}</span>
                {billingPeriod === 'yearly' && <s><bdi dir="ltr">{usd(plan.priceMonthly)}</bdi></s>}
              </div>
              <p>{billingPeriod === 'yearly' ? <>{t('billing.billedAnnuallyPrefix')} <bdi dir="ltr">{usd(price.total)}</bdi> {t('billing.billedAnnuallySuffix')}</> : t('billing.billedMonthly')}</p>
            </div>
            <div className="jsq-pricing-capacity">
              {product !== 'tse' && <span><i className="fa-solid fa-user-group" aria-hidden="true" />{t('labels.seats', { count: plan.features.users, seats: number(plan.features.users) })}</span>}
              {product !== 'ats' && <span><i className="fa-solid fa-address-card" aria-hidden="true" />{t('labels.contactCredits', { credits: number(getPlanCredits(plan)) })}</span>}
            </div>
            <div className="jsq-pricing-actions">
              <button type="button" className="jsq-pricing-primary" disabled={current || Boolean(pendingPlan)}
                aria-busy={pendingPlan === plan.id} onClick={() => onSelectPlan(plan, product, billingPeriod)}>
                {pendingPlan === plan.id && <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />}
                {current ? t('cta.currentPlan') : pendingPlan === plan.id ? t('cta.processing') : t('cta.choosePlan', { plan: copy.title })}
              </button>
              <a className="jsq-pricing-secondary" href={PRICING_SALES_URL} target="_blank" rel="noopener noreferrer">{t('cta.talkToSales')}</a>
            </div>
            <div className="jsq-pricing-features"><h3>{copy.includes || t('labels.features')}</h3><ul>
              {features.map(feature => <li key={feature}><i className="fa-solid fa-check" aria-hidden="true" /><span>{feature}</span></li>)}
            </ul></div>
          </article>;
        })}
      </div>
      <p className="jsq-pricing-note">{product === 'tse' ? t('products.tse.note') : product === 'bundle' ? t('products.bundle.note') : t('billing.currencyNote')}</p>
      <aside className="jsq-pricing-custom"><div><h2>{t('custom.title')}</h2><p>{t(`custom.${product}`)}</p></div>
        <a className="jsq-pricing-secondary" href={PRICING_SALES_URL} target="_blank" rel="noopener noreferrer">{t('cta.talkToSales')}<i className={`fa-solid fa-arrow-${isRTL ? 'left' : 'right'}`} aria-hidden="true" /></a>
      </aside>
    </div>
    {footer && <div className="jsq-pricing-footer">{footer}</div>}
  </section>;
}
